import React, { Suspense, useEffect, useMemo, useState, useCallback } from "react";
import { Routes as RouterRoutes, Route, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// eslint-disable-next-line import/no-extraneous-dependencies
import Keycloak from "keycloak-js";

import { setKeycloak } from "./api/client";

import { actions as authActions } from "./actions/auth";
import { actions as mainActions } from "./actions/main";
import { actions as signUpActions } from "./actions/signup";
import PrivateRoutesContainer from "./containers/privateRoutesContainer";
import Login from "./containers/login";
import { useIntercom, useKeycloakAuth } from "./helpers/hooks";
import { getQueryParams } from "./helpers";
import { lazyWithRetry } from "./helpers/lazyWithRetry";
import { logoutOfKeycloak } from "./helpers/keycloakLogout";
import { recordKeycloakAction } from "./helpers/keycloakAction";
import { Notification } from "./components/common/notification";
import RouteFallback from "./components/common/routeFallback";
import { actions as profileActions } from "./actions/account/profile";

const ProfessionCommunities = lazyWithRetry(() => import("./containers/professionCommunities"));
const ForgotPassword = lazyWithRetry(() => import("./components/passwordReset/forgotPassword"));
const Forbidden = lazyWithRetry(() => import("./components/forbidden"));
const VerificationCode = lazyWithRetry(() => import("./components/passwordReset/verificationCode"));
const SetNewPass = lazyWithRetry(() => import("./components/passwordReset/setNewPass"));
const ResetPasswordSuccess = lazyWithRetry(() => import("./components/passwordReset/resetPasswordSuccess"));
const Communities = lazyWithRetry(() => import("./containers/communities"));
const Pricing = lazyWithRetry(() => import("./containers/pricing"));
const SearchResult = lazyWithRetry(() => import("./containers/searchResult"));
const Landing = lazyWithRetry(() => import("./containers/landing"));
const PublicListing = lazyWithRetry(() => import("./containers/publicListing"));
const PublicOrganizations = lazyWithRetry(() => import("./containers/publicOrganizations"));
const PublicProfile = lazyWithRetry(() => import("./containers/publicProfile"));
const Logout = lazyWithRetry(() => import("./containers/logout"));
const LeadAlreadyPurchased = lazyWithRetry(() =>
  import("./containers/leadAlreadyPurchased").then(module => ({ default: module.LeadAlreadyPurchased }))
);
const LeadExpired = lazyWithRetry(() => import("./containers/leadExpired").then(module => ({ default: module.LeadExpired })));

const keycloak = new Keycloak({
  url: process.env.REACT_APP_KEYCLOAK_URL,
  realm: process.env.REACT_APP_KEYCLOAK_REALM,
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID
});

// Set before init: keycloak-js calls it while init processes the return from a Keycloak action.
keycloak.onActionUpdate = recordKeycloakAction;

setKeycloak(keycloak);

const Routes = () => {
  const profile = useSelector(state => state.account.profile);
  const isLoggedIn = useSelector(state => state.main.isLoggedIn);
  const loginMethods = useSelector(state => state.auth.loginMethods);
  const preloaders = useSelector(state => state.auth.preloaders);
  const errors = useSelector(state => state.auth.errors);
  const listings = useSelector(state => state.listing.memberships);
  const footerInfo = useSelector(state => state.main.footerInfo);
  const socialLinks = useSelector(state => state.main.socialLinks);
  const lastError = useSelector(state => state.requestTracking.lastError);

  const [showError, setShowError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const getProfile = useCallback(() => dispatch(profileActions.getProfile()), [dispatch]);
  const logoutAction = useCallback(() => dispatch(mainActions.logout()), [dispatch]);
  const setLoggedIn = useCallback(isLoggedInValue => dispatch(authActions.setLoggedIn(isLoggedInValue)), [dispatch]);
  const login = useCallback(credentials => dispatch(authActions.login(credentials)), [dispatch]);
  const getPublicProfessions = useCallback(profession => dispatch(signUpActions.getPublicProfessions(profession)), [dispatch]);
  const getHeritages = useCallback(profession => dispatch(signUpActions.getCountries(profession)), [dispatch]);
  const { professionsList, countriesList } = useSelector(state => state.signUp);

  const currentProfession = useMemo(() => {
    return location.pathname
      .replace("-communities", "")
      .replace("/", "")
      .toLowerCase();
  }, [location.pathname]);

  const professionalPathNames = useMemo(() => {
    return countriesList?.length && professionsList?.length
      ? [...countriesList, ...professionsList].map(({ title }) => `/${title.toLowerCase()}-communities`)
      : [];
  }, [countriesList?.length, professionsList?.length]);

  const isCommunities = useMemo(() => location.pathname.includes("-communities"), [location.pathname]);
  const isCommunitiesDefault = useMemo(() => location.pathname.includes("communities"), [location.pathname]);
  const isPublicListingPage = useMemo(() => {
    return (
      location.pathname.includes("public-listing") ||
      location.pathname.includes("public-organizations") ||
      location.pathname.includes("public-profile")
    );
  }, [location.pathname]);
  const queryParams = useMemo(() => getQueryParams(location.search), [location.search]);
  const isGlobalPages = useMemo(() => {
    return ["/pricing", "/communities", "/search-result", "/landing"].includes(location.pathname);
  }, [location.pathname]);

  const logout = () => {
    logoutOfKeycloak(keycloak, { redirectUri: `${window.location.origin}/login` });
    logoutAction();
  };

  useIntercom({ isLoggedIn, listings, profile });

  const { initializedKeycloak } = useKeycloakAuth({ keycloakInstance: keycloak, setLoggedIn });

  useEffect(() => {
    if (isCommunitiesDefault) {
      getPublicProfessions(currentProfession);
      getHeritages(currentProfession);
    }
  }, [location.pathname, getPublicProfessions, getHeritages]); // eslint-disable-line

  useEffect(() => {
    if (!profile?.user_id && isLoggedIn && !profile?.email && (isGlobalPages || isPublicListingPage)) {
      getProfile();
    }

    if (
      !isLoggedIn &&
      initializedKeycloak &&
      !isCommunitiesDefault &&
      !isGlobalPages &&
      !isPublicListingPage &&
      location.pathname !== "/login"
    ) {
      const returnTo = `${location.pathname}${location.search}`;
      const loginSearch = returnTo && returnTo !== "/" ? `?return=${encodeURIComponent(returnTo)}` : "";
      navigate(`/login${loginSearch}`);
    }
  }, [isLoggedIn, getProfile, profile?.email, initializedKeycloak]); // eslint-disable-line

  useEffect(() => {
    if (queryParams?.isAuthenticationError) {
      setShowError(true);

      const urlParams = new URLSearchParams(location.search);
      const currentUrl = window.location.href;
      const newUrl = `${currentUrl.split("?")[0]}?${urlParams.toString()}`.replace("?isAuthenticationError=true", "");

      urlParams.delete("isAuthenticationError");

      window.history.pushState({ path: newUrl }, "", newUrl);
    }
  }, [location.search]); // eslint-disable-line

  return (
    <>
      {showError && (
        <Notification error close={() => setShowError(false)}>
          <span>
            User authentication error. Please contact support.
            {lastError?.requestId ? (
              <>
                {" "}
                Reference ID: <code>{lastError.requestId}</code>
              </>
            ) : null}
          </span>
        </Notification>
      )}

      <Suspense fallback={<RouteFallback />}>
        <RouterRoutes>
          <Route
            path="/login"
            element={
              <Login
                errors={errors}
                isLoggedIn={isLoggedIn}
                login={login}
                loginMethods={loginMethods}
                preloaders={preloaders}
                user={profile}
                keycloak={keycloak}
              />
            }
          />

          <Route path="/logout" element={<Logout handleLogout={logout} />} />

          <Route path="/login/forgot-password" element={<ForgotPassword />} />
          <Route path="/login/verification-code" element={<VerificationCode />} />
          <Route path="/login/set-new-password" element={<SetNewPass />} />
          <Route path="/login/reset-password-success" element={<ResetPasswordSuccess />} />
          <Route path="/forbidden" element={<Forbidden />} />
          {professionalPathNames?.length
            ? professionalPathNames.map(path => <Route key={path} path={path} element={<ProfessionCommunities />} />)
            : null}

          {(!countriesList?.length || !professionsList?.length) && isCommunities && (
            <Route path={`/${currentProfession}-communities`} element={<ProfessionCommunities />} />
          )}

          <Route path="/lead-purchased-already" element={<LeadAlreadyPurchased />} />
          <Route path="/lead-expired" element={<LeadExpired />} />
          <Route path="/communities" element={<Communities />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/search-result" element={<SearchResult />} />

          <Route
            path="/public-listing/:id?"
            element={<PublicListing keycloak={keycloak} socialLinks={socialLinks} footerInfo={footerInfo} />}
          />
          <Route
            path="/public-organizations/:id?"
            element={<PublicOrganizations keycloak={keycloak} socialLinks={socialLinks} footerInfo={footerInfo} />}
          />
          <Route
            path="/public-profile/:id?"
            element={<PublicProfile keycloak={keycloak} socialLinks={socialLinks} footerInfo={footerInfo} />}
          />

          <Route path="/*" element={<PrivateRoutesContainer keycloak={keycloak} isLoggedIn={isLoggedIn} />} />
        </RouterRoutes>
      </Suspense>
    </>
  );
};

export default Routes;
